import { useFeatureFlags } from '@brightdrop/feature-flags-client';
import { useMemo } from 'react';

import {
  getDriveBlockTermsUrl,
  getDualCamTermsUrl,
  getLegalPageTerms,
  getPrivacyStatementUrl,
  getTermsAndConditionsUrl,
  getUserPrivacyLinks,
  getUserTermsLinks,
  type LegalTermConfig,
} from '~/common/constants/legal.constants';
import {
  GM_FLEET_REBRAND_FLAG,
  ONSTAR_FLEET_INTELLIGENCE_REBRAND_FLAG,
} from '~/common/models/featureFlags.model';

type GmFleetRebrandLegal = {
  legalPageTerms: LegalTermConfig[];
  termsAndConditionsUrl: string;
  privacyStatementUrl: string;
  userTermsLinks: ReturnType<typeof getUserTermsLinks>;
  userPrivacyLinks: ReturnType<typeof getUserPrivacyLinks>;
  driveBlockTermsUrl: string;
  dualcamTermsUrl: string;
};

/**
 * Resolves legal document URLs from LaunchDarkly rebrand flags.
 * OnStar Fleet Intelligence docs take precedence when that flag is on; otherwise
 * GM Fleet rebrand docs when `GM_FLEET_REBRAND_FLAG` is on; otherwise legacy.
 */
export const useGmFleetRebrandLegal = (): GmFleetRebrandLegal => {
  const { getFlag } = useFeatureFlags();
  const isGmFleetRebrandEnabled = Boolean(getFlag(GM_FLEET_REBRAND_FLAG));
  const isOnStarFleetIntelligenceRebrandEnabled = Boolean(
    getFlag(ONSTAR_FLEET_INTELLIGENCE_REBRAND_FLAG)
  );

  return useMemo(
    () => ({
      legalPageTerms: getLegalPageTerms(
        isGmFleetRebrandEnabled,
        isOnStarFleetIntelligenceRebrandEnabled
      ),
      termsAndConditionsUrl: getTermsAndConditionsUrl(
        isGmFleetRebrandEnabled,
        isOnStarFleetIntelligenceRebrandEnabled
      ),
      privacyStatementUrl: getPrivacyStatementUrl(
        isGmFleetRebrandEnabled,
        isOnStarFleetIntelligenceRebrandEnabled
      ),
      userTermsLinks: getUserTermsLinks(
        isGmFleetRebrandEnabled,
        isOnStarFleetIntelligenceRebrandEnabled
      ),
      userPrivacyLinks: getUserPrivacyLinks(
        isGmFleetRebrandEnabled,
        isOnStarFleetIntelligenceRebrandEnabled
      ),
      driveBlockTermsUrl: getDriveBlockTermsUrl(
        isGmFleetRebrandEnabled,
        isOnStarFleetIntelligenceRebrandEnabled
      ),
      dualcamTermsUrl: getDualCamTermsUrl(
        isGmFleetRebrandEnabled,
        isOnStarFleetIntelligenceRebrandEnabled
      ),
    }),
    [isGmFleetRebrandEnabled, isOnStarFleetIntelligenceRebrandEnabled]
  );
};
